<script setup>
import { computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import {
  mentionQueryAt,
  insertMention,
  mentionedIds,
  filterAgents,
} from './helpers/mentions';

// onSubmit é função (não evento) para a caixa só limpar depois que o pai salvou;
// se o pai lançar erro, o texto fica para a pessoa tentar de novo.
const props = defineProps({
  agents: { type: Array, default: () => [] },
  onSubmit: { type: Function, required: true },
  initialContent: { type: String, default: '' },
  submitLabel: { type: String, default: '' },
  cancelable: { type: Boolean, default: false },
});

const emit = defineEmits(['cancel']);

const { t } = useI18n();
const textarea = ref(null);
const content = ref(props.initialContent);
const query = ref(null);
const highlighted = ref(0);
const isSending = ref(false);

const suggestions = computed(() =>
  query.value === null ? [] : filterAgents(props.agents, query.value)
);

const updateQuery = () => {
  const el = textarea.value;
  query.value = el ? mentionQueryAt(content.value, el.selectionStart) : null;
  highlighted.value = 0;
};

const pick = async agent => {
  const el = textarea.value;
  const result = insertMention(content.value, el.selectionStart, agent.name);
  content.value = result.text;
  query.value = null;
  await nextTick();
  el.focus();
  el.setSelectionRange(result.caret, result.caret);
};

const submit = async () => {
  const text = content.value.trim();
  if (!text || isSending.value) return;
  isSending.value = true;
  try {
    await props.onSubmit({
      content: text,
      mentionedUserIds: mentionedIds(text, props.agents),
    });
    if (!props.cancelable) content.value = '';
  } catch {
    // o pai já avisou o erro; mantém o texto
  } finally {
    isSending.value = false;
  }
};

const onKeydown = event => {
  // com a lista aberta, Esc fecha só a lista (senão fecharia o card inteiro e perderia o rascunho)
  if (query.value !== null && event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    query.value = null;
    return;
  }
  if (suggestions.value.length) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      highlighted.value = (highlighted.value + 1) % suggestions.value.length;
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      highlighted.value =
        (highlighted.value - 1 + suggestions.value.length) %
        suggestions.value.length;
      return;
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      pick(suggestions.value[highlighted.value]);
      return;
    }
  }
  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) submit();
};
</script>

<template>
  <div class="relative flex flex-col gap-2">
    <textarea
      ref="textarea"
      v-model="content"
      rows="2"
      class="w-full px-3 py-2 text-sm border rounded-lg outline-none resize-y bg-n-alpha-black2 text-n-slate-12 border-n-weak focus:border-n-brand"
      :placeholder="t('TASKS.COMMENTS.PLACEHOLDER')"
      @input="updateQuery"
      @click="updateQuery"
      @keydown="onKeydown"
    />
    <div
      v-if="query !== null"
      class="absolute left-0 z-10 w-56 p-1 border rounded-lg shadow-lg top-full bg-n-solid-2 border-n-weak"
    >
      <p v-if="!suggestions.length" class="px-2 py-1.5 text-sm text-n-slate-10">
        {{ t('TASKS.COMMENTS.NO_MATCH') }}
      </p>
      <button
        v-for="(agent, index) in suggestions"
        :key="agent.id"
        class="flex items-center w-full gap-2 px-2 py-1.5 text-sm rounded-md text-n-slate-12"
        :class="index === highlighted ? 'bg-n-alpha-2' : 'hover:bg-n-alpha-2'"
        @mousedown.prevent="pick(agent)"
      >
        <Avatar
          :name="agent.name"
          :src="agent.thumbnail"
          :size="20"
          rounded-full
        />
        <span class="truncate">{{ agent.name }}</span>
      </button>
    </div>
    <div class="flex items-center gap-2">
      <button
        class="px-3 py-1.5 text-sm font-medium text-white rounded-lg bg-n-brand hover:opacity-90 disabled:opacity-50"
        :disabled="!content.trim() || isSending"
        @click="submit"
      >
        {{ submitLabel || t('TASKS.COMMENTS.SEND') }}
      </button>
      <button
        v-if="cancelable"
        class="px-3 py-1.5 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
        @click="emit('cancel')"
      >
        {{ t('TASKS.COMMENTS.CANCEL') }}
      </button>
    </div>
  </div>
</template>
